import { alpha, createTheme, Grow, Slide } from "@mui/material";
import type { Shadows } from "@mui/material/styles";

/* ==========================================================================
   Design System — Felix na Bolsa (app)
   Fonte de verdade: ./DESIGN.md na raiz do repo.
   Os tokens são contrato compartilhado com `felix-externo` e `pulse-web`:
   mudou cor/fonte/radius/sombra aqui, muda nos outros dois.
   ========================================================================== */

declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/Icon" {
  interface IconPropsColorOverrides {
    tertiary: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
    charts: Array<string>;
    complementary: {
      positives: Array<string>;
      negatives: Array<string>;
      neutrals: Array<string>;
    };
    /** Tinta do accent — troca por tema. NUNCA use `primary.main` como cor de
     *  texto sobre superfície escura (2,57:1, reprova em WCAG). */
    accentInk: string;
    /** Tinta da lavanda (accent 2) — troca por tema. Mesmo motivo do
     *  `accentInk`: a lavanda da marca é preenchimento, não tinta. */
    tertiaryInk: string;
    /** Rampa de tinta em opacidades fixas. Use no lugar de inventar cinza. */
    ink: {
      full: string;
      60: string;
      40: string;
      20: string;
      "08": string;
    };
    /** Tints de chip de status, por tema. */
    statusChip: {
      positive: { bg: string; fg: string };
      accent: { bg: string; fg: string };
      warn: { bg: string; fg: string };
      destructive: { bg: string; fg: string };
      neutral: { bg: string; fg: string };
    };
  }
  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
    charts: Array<string>;
    complementary: {
      positives: Array<string>;
      negatives: Array<string>;
      neutrals: Array<string>;
    };
    accentInk: string;
    tertiaryInk: string;
    ink: {
      full: string;
      60: string;
      40: string;
      20: string;
      "08": string;
    };
    statusChip: {
      positive: { bg: string; fg: string };
      accent: { bg: string; fg: string };
      warn: { bg: string; fg: string };
      destructive: { bg: string; fg: string };
      neutral: { bg: string; fg: string };
    };
  }
  interface TypeText {
    primaryWhite: string;
    secondaryWhite: string;
    primaryBlack: string;
    secondaryBlack: string;
  }
  interface TypographyVariants {
    /** Número/dado em mono tabular. Assinatura #1 do app. */
    dataMono: React.CSSProperties;
    /** Rótulo mono UPPERCASE, tracking +0.12em. */
    labelMono: React.CSSProperties;
    /** Eyebrow de seção, mono UPPERCASE, tracking +0.18em. */
    eyebrow: React.CSSProperties;
    /** Display — só telas de entrada (login, registro, vazio grande). */
    display: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    dataMono?: React.CSSProperties;
    labelMono?: React.CSSProperties;
    eyebrow?: React.CSSProperties;
    display?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    dataMono: true;
    labelMono: true;
    eyebrow: true;
    display: true;
  }
}

/* --------------------------------------------------------------------------
   TOKENS (DESIGN.md → Themes & Color)
   -------------------------------------------------------------------------- */

const FONT_SANS =
  "'Mabry Pro', -apple-system, 'Helvetica Neue', Arial, sans-serif";
const FONT_MONO =
  "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const ACCENT = "#4846D2";
const ACCENT_HOVER = "#3E3CC0";
const ACCENT_2 = "#A465DF";
/** Lavanda escurecida só para PREENCHIMENTO de botão: o `#A465DF` da marca é
 *  claro demais para tinta clara (creme rende 3,35:1). Mesma matiz (271°),
 *  4,84:1 com o creme. O token da marca segue intacto para tint/badge/chip. */
const ACCENT_2_FILL = "#8451B3";
const ACCENT_2_FILL_HOVER = "#7A4BA6";
const POSITIVE = "#3D7A2E";
const WARN = "#C66A1A";
const DESTRUCTIVE = "#B23A2A";

/** Accent clareado para uso como TINTA sobre superfície escura (7,4:1). */
const ACCENT_INK_DARK = "#9C9AF0";

/** Lavanda como TINTA (borda/rótulo de botão outlined, ícone). O `#A465DF` da
 *  marca rende 2,9:1 sobre o creme e reprova; o `#8451B3` do preenchimento
 *  rende 5,2:1 no claro mas só 2,6:1 sobre superfície escura — daí o par. */
const ACCENT_2_INK = ACCENT_2_FILL;
const ACCENT_2_INK_DARK = "#C39BEB";

const PAPER_INK = "#F3F0E9"; // texto sobre preenchimento accent/destructive sólido

const light = {
  bg: "#F3F0E9",
  surface: "#FAF8F3",
  ink: "#1B160E",
  ink60: "rgba(27, 22, 14, 0.62)",
  ink40: "rgba(27, 22, 14, 0.42)",
  ink20: "rgba(27, 22, 14, 0.18)",
  ink08: "rgba(27, 22, 14, 0.08)",
  rule: "rgba(27, 22, 14, 0.14)",
};

const dark = {
  bg: "#141210",
  surface: "#1C1915",
  ink: "#F3F0E9",
  ink60: "rgba(243, 240, 233, 0.62)",
  ink40: "rgba(243, 240, 233, 0.42)",
  ink20: "rgba(243, 240, 233, 0.22)",
  ink08: "rgba(243, 240, 233, 0.08)",
  rule: "rgba(243, 240, 233, 0.14)",
};

const statusChipLight = {
  positive: { bg: "#E7EEDF", fg: "#2F5F23" },
  accent: { bg: "#E6E5F8", fg: "#3633B8" },
  warn: { bg: "#F6E9D6", fg: "#8F4D12" },
  destructive: { bg: "#F4E0D9", fg: "#8F2E1A" },
  neutral: { bg: "#ECE8DF", fg: light.ink60 },
};

const statusChipDark = {
  positive: { bg: "rgba(61, 122, 46, 0.18)", fg: "#86C46F" },
  accent: { bg: "rgba(72, 70, 210, 0.20)", fg: "#9C9AF0" },
  warn: { bg: "rgba(198, 106, 26, 0.18)", fg: "#E0A766" },
  destructive: { bg: "rgba(178, 58, 42, 0.20)", fg: "#E08A78" },
  neutral: { bg: dark.ink08, fg: dark.ink60 },
};

/** Séries de dados dos gráficos — fora do sistema de tema por design:
 *  série de dado não troca com light/dark. Ver DESIGN.md → Gráficos. */
const CHART_SERIES = {
  complementary: {
    positives: [
      "#598b1b",
      "#5d9529",
      "#60a036",
      "#63aa43",
      "#65b54f",
      "#68bf5c",
      "#6aca68",
      "#6bd574",
    ],
    negatives: [
      "#c14047",
      "#c7544c",
      "#cd6650",
      "#d27755",
      "#d7885a",
      "#dc985f",
      "#e0a863",
      "#e4b868",
    ],
    neutrals: [
      "#411daf",
      "#543db7",
      "#6458bf",
      "#7173c7",
      "#7c8ecf",
      "#85a9d6",
      "#8dc4dd",
      "#93e0e4",
    ],
  },
  charts: [
    "#3f27ac",
    "#473bb4",
    "#4f4fbc",
    "#5763c4",
    "#6078cc",
    "#688cd4",
    "#70a0dc",
    "#78b4e4",
  ],
};

/* Elevação quente e sutil. A hierarquia do app vem de hairline, não de sombra —
   por isso Card/Paper default é elevation 0 + borda. Ver DESIGN.md → Elevation. */
const SHADOW_SM = "0 1px 4px rgba(0, 0, 0, 0.08)";
const SHADOW_FLOAT =
  "0 1px 0 rgba(0, 0, 0, 0.02), 0 40px 80px -40px rgba(27, 22, 14, 0.15)";
const SHADOW_OVERLAY = "0 20px 40px -20px rgba(27, 22, 14, 0.3)";

const warmShadows = [
  "none",
  SHADOW_SM,
  SHADOW_SM,
  "0 1px 4px rgba(0, 0, 0, 0.12)",
  SHADOW_FLOAT,
  SHADOW_FLOAT,
  SHADOW_FLOAT,
  SHADOW_FLOAT,
  SHADOW_FLOAT,
  ...Array(16).fill(SHADOW_OVERLAY),
] as unknown as Shadows;

export const radius = {
  xs: 2,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 20,
  pill: 999,
  round: "50%",
} as const;

/* --------------------------------------------------------------------------
   BASE THEME — tipografia, forma e comportamento comuns aos dois modos
   -------------------------------------------------------------------------- */

const baseTheme = createTheme({
  shape: {
    borderRadius: radius.md,
  },

  shadows: warmShadows,

  typography: {
    fontFamily: FONT_SANS,
    // Corpo 14px/1.5 — interface densa. O site externo usa 16px/1.6.
    fontSize: 14,

    display: {
      fontFamily: FONT_SANS,
      fontWeight: 500,
      fontSize: "clamp(34px, 4vw, 56px)",
      lineHeight: 1.05,
      letterSpacing: "-0.03em",
    },
    h1: {
      fontWeight: 600,
      fontSize: "1.75rem", // 28px
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.375rem", // 22px
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.125rem", // 18px
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1rem", // 16px
      lineHeight: 1.4,
      letterSpacing: "-0.01em",
    },
    h5: {
      fontWeight: 600,
      fontSize: "0.9375rem", // 15px
      lineHeight: 1.4,
      letterSpacing: "0em",
    },
    h6: {
      fontWeight: 600,
      fontSize: "0.875rem", // 14px
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    body1: {
      fontWeight: 400,
      fontSize: "0.875rem", // 14px
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.8125rem", // 13px
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    button: {
      fontWeight: 500,
      fontSize: "0.875rem", // 14px
      lineHeight: 1,
      letterSpacing: "0em",
      textTransform: "none",
    },
    // ATENÇÃO: `caption` e `overline` do MUI são TEXTO CORRIDO pequeno — o
    // FormHelperText usa `caption`. Deixá-las em mono UPPERCASE fazia toda
    // mensagem de ajuda do app virar frase gritada em maiúscula. O "Label /
    // Caption" mono do DESIGN.md é outra coisa: são as variantes explícitas
    // `labelMono` e `eyebrow`, abaixo.
    caption: {
      fontWeight: 400,
      fontSize: "0.75rem", // 12px
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    overline: {
      fontWeight: 500,
      fontSize: "0.6875rem", // 11px
      lineHeight: 1.4,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    // Variantes próprias — use estas em vez de montar sx à mão
    dataMono: {
      fontFamily: FONT_MONO,
      fontWeight: 400,
      fontSize: "0.875rem", // 14px
      lineHeight: 1.4,
      letterSpacing: "-0.01em",
      fontVariantNumeric: "tabular-nums",
    },
    labelMono: {
      fontFamily: FONT_MONO,
      fontWeight: 500,
      fontSize: "0.6875rem", // 11px
      lineHeight: 1.3,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    },
    eyebrow: {
      fontFamily: FONT_MONO,
      fontWeight: 500,
      fontSize: "0.6875rem", // 11px
      lineHeight: 1.2,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
    },
  },
});

/* --------------------------------------------------------------------------
   COMPONENT OVERRIDES — dependem do tema resolvido, então recebem `theme`
   -------------------------------------------------------------------------- */

const buildComponents = (t: typeof light | typeof dark) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: t.bg,
        color: t.ink,
      },
      // Traço fino em TODO ícone Tabler do app. CSS vence o atributo
      // `stroke-width` que o Tabler escreve no SVG, então afina tudo sem
      // precisar passar `stroke` em cada ícone. Par com a decisão de usar
      // só a família outline (nada de `*Filled`).
      ".tabler-icon": { strokeWidth: 1.5 },
      // Foco de teclado padronizado. Usa accentInk: o accent sólido dá 2,57:1
      // sobre superfície escura e reprova nos 3:1 do WCAG 2.2.
      ":focus-visible": {
        outline: `2px solid ${t === dark ? ACCENT_INK_DARK : ACCENT}`,
        outlineOffset: 2,
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*": {
          animationDuration: "0.01ms !important",
          transitionDuration: "0.01ms !important",
        },
      },
    },
  },

  MuiTypography: {
    defaultProps: {
      variantMapping: {
        display: "h1",
        dataMono: "span",
        labelMono: "span",
        eyebrow: "p",
      },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      // minHeight explícito: a tipografia de botão usa lineHeight 1 (aperta o
      // texto), então botão só-texto ficava visivelmente mais baixo que botão
      // com ícone. A altura passa a ser a mesma, com ou sem ícone.
      root: {
        borderRadius: radius.pill,
        paddingInline: 20,
        minHeight: 36,
      },
      sizeSmall: { minHeight: 30, paddingInline: 14 },
      sizeLarge: { minHeight: 44, paddingInline: 24 },
      containedPrimary: {
        color: PAPER_INK,
        "&:hover": { backgroundColor: ACCENT_HOVER },
      },
      // Botão roxo usa a lavanda escurecida, para a tinta poder ser clara.
      contained: ({ ownerState }: any) =>
        ownerState.color === "tertiary"
          ? {
              backgroundColor: ACCENT_2_FILL,
              color: PAPER_INK,
              "&:hover": { backgroundColor: ACCENT_2_FILL_HOVER },
            }
          : {},
      // Só o outlined NEUTRO (color default) vira ink. Se o chamador pediu
      // `color="error"` num botão destrutivo, a cor semântica tem que sobreviver
      // — senão "excluir" fica igual a "baixar".
      outlined: ({ ownerState }: any) => ({
        ...(ownerState.color === "primary" && {
          borderColor: t.ink20,
          color: t.ink,
        }),
        // Outlined é TINTA, não preenchimento: a lavanda precisa da versão
        // clareada no dark, igual ao que o `text`/`primary` faz com o accent.
        ...(ownerState.color === "tertiary" && {
          borderColor: t === dark ? ACCENT_2_INK_DARK : ACCENT_2_INK,
          color: t === dark ? ACCENT_2_INK_DARK : ACCENT_2_INK,
        }),
        "&:hover": { backgroundColor: t.ink08 },
      }),
      // Botão `text` é TINTA, não preenchimento: precisa do accent clareado no
      // dark (o accent sólido dá 2,57:1 ali).
      text: ({ ownerState }: any) => ({
        ...(ownerState.color === "primary" && {
          color: t === dark ? ACCENT_INK_DARK : ACCENT,
        }),
        "&:hover": { backgroundColor: t.ink08 },
      }),
    },
  },

  MuiFab: {
    styleOverrides: { root: { boxShadow: "none" } },
  },

  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: radius.pill,
        // Trilha = `bg` (o fundo da página), segmento ativo = `surface`.
        // Assim o ativo é SEMPRE um passo mais claro que a trilha nos dois
        // temas. Com a trilha em `ink-08` o dark invertia: `surface` (#1C1915)
        // fica mais escuro que ink-08 sobre bg (~#262421), e o segmento
        // selecionado parecia afundado enquanto o inativo parecia ativo.
        backgroundColor: t.bg,
        border: `1px solid ${t.rule}`,
      },
      groupedHorizontal: { borderRadius: `${radius.pill}px !important` },
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        fontWeight: 500,
        borderRadius: radius.pill,
        borderWidth: 0,
        color: t.ink60,
        "&.Mui-selected": {
          fontWeight: 600,
          borderRadius: radius.pill,
          // Segmento ativo sobe pra surface com sombra — não vira bloco de cor.
          backgroundColor: t.surface,
          color: t.ink,
          boxShadow: SHADOW_SM,
          "&:hover": { backgroundColor: t.surface },
        },
      },
    },
  },

  // Card e Paper: hierarquia por hairline, não por sombra.
  MuiCard: {
    defaultProps: { variant: "elevation" as const, elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: t.surface,
        border: `1px solid ${t.rule}`,
        borderRadius: radius.lg,
        backgroundImage: "none",
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" },
      outlined: { borderColor: t.rule },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: t.surface,
        border: `1px solid ${t.rule}`,
        borderRadius: radius.xl,
        boxShadow: SHADOW_OVERLAY,
        backgroundImage: "none",
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: t.surface,
        border: `1px solid ${t.rule}`,
        borderRadius: radius.lg,
        boxShadow: SHADOW_OVERLAY,
        backgroundImage: "none",
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: t.surface,
        border: `1px solid ${t.rule}`,
        borderRadius: radius.lg,
        boxShadow: SHADOW_OVERLAY,
        backgroundImage: "none",
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        alignSelf: "baseline",
        borderRadius: radius.pill,
        fontFamily: FONT_MONO,
        fontSize: "0.6875rem",
        fontWeight: 500,
        letterSpacing: "0.04em",
        height: 24,
      },
      label: { fontWeight: 500 },
      outlined: { borderColor: t.ink20 },
      // Chip clicável (ticker de ativo, filtro) tem que PARECER clicável: o
      // override de chip acima deixa todo chip com cara de status passivo.
      clickable: {
        cursor: "pointer",
        border: `1px solid ${t.rule}`,
        transition:
          "background-color 120ms ease-out, border-color 120ms ease-out",
        "&:hover": {
          backgroundColor: t.ink08,
          borderColor: t.ink20,
        },
      },
    },
  },

  MuiDivider: {
    styleOverrides: { root: { borderColor: t.rule } },
  },

  // Tabelas — o componente mais importante do app (DESIGN.md → Tabelas)
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottomColor: t.rule,
        fontSize: "0.875rem",
      },
      head: {
        fontFamily: FONT_MONO,
        fontSize: "0.6875rem",
        fontWeight: 500,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: t.ink60,
        backgroundColor: t.surface,
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: { "&:hover": { backgroundColor: t.ink08 } },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: t.surface,
        borderRadius: radius.md,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: t.rule },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.ink20 },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: t === dark ? ACCENT_INK_DARK : ACCENT,
          borderWidth: 1,
        },
      },
      input: { "&::placeholder": { color: t.ink40, opacity: 1 } },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ ownerState, theme }: any) => ({
        borderRadius: radius.lg,
        ...(ownerState.variant === "filled" && {
          color: ownerState.severity
            ? theme.palette[ownerState.severity].contrastText
            : PAPER_INK,
        }),
      }),
    },
  },

  MuiSnackbar: {
    defaultProps: {
      TransitionComponent: (props: any) => <Slide direction="up" {...props} />,
    },
  },

  MuiTooltip: {
    defaultProps: { TransitionComponent: Grow },
    styleOverrides: {
      tooltip: {
        backgroundColor: t.surface,
        color: t.ink,
        fontFamily: FONT_MONO,
        fontSize: "0.6875rem",
        letterSpacing: "0.04em",
        fontWeight: 500,
        border: `1px solid ${t.rule}`,
        borderRadius: radius.sm,
        boxShadow: SHADOW_OVERLAY,
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: radius.pill, height: 6, backgroundColor: t.ink08 },
      bar: { borderRadius: radius.pill },
    },
  },
});

/* --------------------------------------------------------------------------
   LIGHT
   -------------------------------------------------------------------------- */

const lightTheme = createTheme(baseTheme, {
  components: buildComponents(light),
  palette: {
    mode: "light",
    primary: baseTheme.palette.augmentColor({
      color: { main: ACCENT, contrastText: PAPER_INK },
    }),
    secondary: baseTheme.palette.augmentColor({
      color: { main: light.ink, contrastText: PAPER_INK },
    }),
    tertiary: baseTheme.palette.augmentColor({
      // Tinta ESCURA sobre a lavanda: creme sobre #A465DF rende 3,35:1 e
      // reprova; #141210 rende 4,9:1. A lavanda é clara demais para tinta clara.
      color: { main: ACCENT_2, contrastText: "#141210" },
      name: "tertiary",
    }),
    success: baseTheme.palette.augmentColor({
      color: { main: POSITIVE, contrastText: PAPER_INK },
    }),
    error: baseTheme.palette.augmentColor({
      color: { main: DESTRUCTIVE, contrastText: PAPER_INK },
    }),
    warning: baseTheme.palette.augmentColor({
      color: { main: WARN, contrastText: PAPER_INK },
    }),
    // O sistema não define azul próprio — `info` é mapeado no accent.
    info: baseTheme.palette.augmentColor({
      color: { main: ACCENT, contrastText: PAPER_INK },
    }),
    accentInk: ACCENT,
    tertiaryInk: ACCENT_2_INK,
    ink: {
      full: light.ink,
      60: light.ink60,
      40: light.ink40,
      20: light.ink20,
      "08": light.ink08,
    },
    statusChip: statusChipLight,
    ...CHART_SERIES,
    background: {
      default: light.bg,
      paper: light.surface,
    },
    action: {
      active: light.ink60,
      hover: light.ink08,
      hoverOpacity: 0.08,
      selected: light.ink08,
      selectedOpacity: 0.08,
      disabled: light.ink40,
      disabledBackground: light.ink08,
      disabledOpacity: 0.38,
      focus: light.ink08,
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
    divider: light.rule,
    text: {
      primary: light.ink,
      secondary: light.ink60,
      disabled: light.ink40,
      primaryWhite: PAPER_INK,
      secondaryWhite: alpha(PAPER_INK, 0.62),
      primaryBlack: "#1B160E",
      secondaryBlack: "rgba(27, 22, 14, 0.62)",
    },
  },
});

/* --------------------------------------------------------------------------
   DARK
   -------------------------------------------------------------------------- */

const darkTheme = createTheme(baseTheme, {
  components: buildComponents(dark),
  palette: {
    mode: "dark",
    // main = preenchimento (fica no indigo da marca); light = tinta clareada.
    primary: baseTheme.palette.augmentColor({
      color: {
        main: ACCENT,
        light: ACCENT_INK_DARK,
        contrastText: PAPER_INK,
      },
    }),
    secondary: baseTheme.palette.augmentColor({
      color: { main: dark.ink, contrastText: "#141210" },
    }),
    tertiary: baseTheme.palette.augmentColor({
      // Tinta ESCURA sobre a lavanda: creme sobre #A465DF rende 3,35:1 e
      // reprova; #141210 rende 4,9:1. A lavanda é clara demais para tinta clara.
      color: { main: ACCENT_2, contrastText: "#141210" },
      name: "tertiary",
    }),
    // Semânticas clareadas para legibilidade sobre superfície escura.
    success: baseTheme.palette.augmentColor({
      color: { main: "#86C46F", contrastText: "#141210" },
    }),
    error: baseTheme.palette.augmentColor({
      color: { main: "#E08A78", contrastText: "#141210" },
    }),
    warning: baseTheme.palette.augmentColor({
      color: { main: "#E0A766", contrastText: "#141210" },
    }),
    info: baseTheme.palette.augmentColor({
      color: { main: ACCENT_INK_DARK, contrastText: "#141210" },
    }),
    accentInk: ACCENT_INK_DARK,
    tertiaryInk: ACCENT_2_INK_DARK,
    ink: {
      full: dark.ink,
      60: dark.ink60,
      40: dark.ink40,
      20: dark.ink20,
      "08": dark.ink08,
    },
    statusChip: statusChipDark,
    ...CHART_SERIES,
    background: {
      default: dark.bg,
      paper: dark.surface,
    },
    action: {
      active: dark.ink60,
      hover: dark.ink08,
      hoverOpacity: 0.08,
      selected: dark.ink08,
      selectedOpacity: 0.08,
      disabled: dark.ink40,
      disabledBackground: dark.ink08,
      disabledOpacity: 0.38,
      focus: dark.ink08,
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
    divider: dark.rule,
    text: {
      primary: dark.ink,
      secondary: dark.ink60,
      disabled: dark.ink40,
      primaryWhite: PAPER_INK,
      secondaryWhite: alpha(PAPER_INK, 0.62),
      primaryBlack: "#1B160E",
      secondaryBlack: "rgba(27, 22, 14, 0.62)",
    },
  },
});

/** Tokens crus, para os poucos casos que não passam pelo `theme.palette`
 *  (ex.: tema de biblioteca de gráfico que só aceita string). */
export const felixTokens = {
  fontSans: FONT_SANS,
  fontMono: FONT_MONO,
  accent: ACCENT,
  accentHover: ACCENT_HOVER,
  accent2: ACCENT_2,
  accentInkDark: ACCENT_INK_DARK,
  tertiaryInk: ACCENT_2_INK,
  tertiaryInkDark: ACCENT_2_INK_DARK,
  positive: POSITIVE,
  warn: WARN,
  destructive: DESTRUCTIVE,
  paperInk: PAPER_INK,
  light,
  dark,
  radius,
  shadow: {
    sm: SHADOW_SM,
    float: SHADOW_FLOAT,
    overlay: SHADOW_OVERLAY,
  },
} as const;

export default createTheme(lightTheme);

export { lightTheme, darkTheme };
